import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card, PageHeader, Skeleton } from "@/components/ui";

interface NotificationRule {
  notification_type: string;
  recipient_kind: string;
  enabled: boolean;
}

const typeLabels: Record<string, string> = {
  "wallet.topup_succeeded": "Wallet top-up succeeded",
  "wallet.topup_failed": "Wallet top-up failed",
  "shipment.booked": "Shipment booked",
  "shipment.tracking_update": "Tracking status update",
};

const kindLabels: Record<string, string> = {
  account_owner: "Account owner",
  account_admins: "Account admins",
  all_account_users: "All account users",
  booking_recipient: "Booking recipient",
  ops_manager: "Ops manager",
  client_care: "Client care",
};

const TYPE_ORDER = Object.keys(typeLabels);
const KIND_ORDER = Object.keys(kindLabels);

/**
 * Who receives which notification.
 *
 * A matrix, not a list of toggles per screen: the question an operator
 * actually has is "does the ops team see tracking exceptions", which is a
 * row-and-column lookup, not something worth four separate pages to answer.
 */
export function PlatformNotifications() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["notification-rules"],
    queryFn: () => api.get<{ rules: NotificationRule[] }>("/v1/admin/notifications/rules"),
  });

  const toggle = useMutation({
    mutationFn: (rule: NotificationRule) =>
      api.put(`/v1/admin/notifications/rules/${rule.notification_type}/${rule.recipient_kind}`, {
        enabled: !rule.enabled,
      }),
    // Optimistic: a checkbox that waits for a round trip before it moves
    // feels broken, and worst case a refetch corrects it a moment later.
    onMutate: async (rule) => {
      await queryClient.cancelQueries({ queryKey: ["notification-rules"] });
      const previous = queryClient.getQueryData<{ rules: NotificationRule[] }>(["notification-rules"]);
      queryClient.setQueryData<{ rules: NotificationRule[] }>(["notification-rules"], (current) =>
        current
          ? {
              rules: current.rules.map((r) =>
                r.notification_type === rule.notification_type && r.recipient_kind === rule.recipient_kind
                  ? { ...r, enabled: !r.enabled }
                  : r,
              ),
            }
          : current,
      );
      return { previous };
    },
    onError: (_err, _rule, context) => {
      if (context?.previous) queryClient.setQueryData(["notification-rules"], context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["notification-rules"] }),
  });

  if (isLoading || !data) {
    return (
      <>
        <PageHeader title="Notifications" subtitle="Who receives which email, per notification type." />
        <Card>
          <Skeleton className="h-64 w-full" />
        </Card>
      </>
    );
  }

  const byCell = new Map(data.rules.map((r) => [`${r.notification_type}|${r.recipient_kind}`, r]));

  return (
    <>
      <PageHeader title="Notifications" subtitle="Who receives which email, per notification type." />

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-ink-400">
                <th className="py-2 pr-4 text-left">Notification</th>
                {KIND_ORDER.map((kind) => (
                  <th key={kind} className="px-2 py-2 text-center font-medium">
                    {kindLabels[kind]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-200">
              {TYPE_ORDER.map((type) => (
                <tr key={type}>
                  <td className="py-3 pr-4 font-medium">{typeLabels[type]}</td>
                  {KIND_ORDER.map((kind) => {
                    const rule = byCell.get(`${type}|${kind}`) ?? { notification_type: type, recipient_kind: kind, enabled: false };
                    return (
                      <td key={kind} className="px-2 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={rule.enabled}
                          onChange={() => toggle.mutate(rule)}
                          className="h-4 w-4 rounded border-ink-300 text-brand"
                          aria-label={`${typeLabels[type]} to ${kindLabels[kind]}`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-xs text-ink-400">
          Booking recipient is the delivery contact on the shipment itself, not the account holder.
          Ops manager and client care are WeShip staff.
        </p>
      </Card>
    </>
  );
}
